import { ref, computed } from 'vue'
import { useAuthStore } from '../stores/auth'
import { useGameStore } from '../stores/game'
import { getTheme, type Theme, type ThemeColors, type ThemeMode } from '../config/themes'
import { resolveThemeName, loadThemeWithFallback } from '../utils/themeLoader'
const currentThemeName = ref<string>('default')
const currentTheme = ref<Theme>(getTheme('default'))
let isInitializing = false // Prevent concurrent initialization
let lastInitializedAccount: string | null = null // Track last account theme was initialized for

/** Theme from current page URL (search or hash). Use this so URL theme wins over game-settings. */
export function getThemeParamFromUrl(): string | null {
  try {
    const search = new URLSearchParams(window.location.search)
    const fromSearch = search.get('theme')
    if (fromSearch) return fromSearch.trim() || null
    const hash = window.location.hash
    const hashIdx = hash.indexOf('?')
    if (hashIdx !== -1) {
      const fromHash = new URLSearchParams(hash.slice(hashIdx)).get('theme')
      if (fromHash) return fromHash.trim() || null
    }
  } catch {
    // ignore
  }
  return null
}

function getCurrentLocale(): string {
  try {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('locale') || 'en'
  } catch {
    return 'en'
  }
}

function getActiveThemeMode(): ThemeMode {
  return document.documentElement.classList.contains('theme-dark') ? 'dark' : 'light'
}

/** True when `currentTheme` came from a JSON file (modes + colors), not the bundled fallback. */
function isFileBackedTheme(theme: Theme): boolean {
  return Boolean(theme.modes && theme.colors)
}

function resolveThemeColors(theme: Theme, mode: ThemeMode): ThemeColors {
  const resolved = {
    ...(theme.colors || {}),
    ...(theme.modes?.[mode] || {}),
  }
  if (!resolved) {
    return getTheme('default').colors as ThemeColors
  }
  return resolved as ThemeColors
}

function applyTheme(theme: Theme, mode: ThemeMode = getActiveThemeMode()): void {
  const root = document.documentElement
  const resolvedColors = resolveThemeColors(theme, mode)
  
  Object.entries(resolvedColors).forEach(([key, value]) => {
    if (!value) return
    const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase()
    root.style.setProperty(`--${cssKey}`, value)
  })
  
  Object.entries(theme.typography).forEach(([key, value]) => {
    const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase()
    root.style.setProperty(`--typography-${cssKey}`, String(value))
  })
  
  Object.entries(theme.spacing).forEach(([key, value]) => {
    root.style.setProperty(`--spacing-${key}`, value)
  })
  
  Object.entries(theme.borderRadius).forEach(([key, value]) => {
    root.style.setProperty(`--radius-${key}`, value)
  })
  
  Object.entries(theme.shadows).forEach(([key, value]) => {
    root.style.setProperty(`--shadow-${key}`, value)
  })
  
  root.setAttribute('data-account-theme', theme.name)
}

/** Apply the active account palette for the current light/dark mode. */
export function syncAccountThemeCssVariables(): void {
  applyTheme(currentTheme.value, getActiveThemeMode())
}

export async function initializeTheme(force = false): Promise<void> {
  if (isInitializing && !force) {
    while (isInitializing) {
      await new Promise(resolve => setTimeout(resolve, 50))
    }
    return
  }

  try {
    isInitializing = true

    const urlTheme = getThemeParamFromUrl() || undefined
    const authStore = useAuthStore()
    const account = authStore.account || sessionStorage.getItem('auth_account')

    if (!force && lastInitializedAccount === account && currentThemeName.value !== 'default') {
      if (!urlTheme || resolveThemeName(urlTheme) === currentThemeName.value) {
        isInitializing = false
        return
      }
    }

    // 1. URL `theme` wins. 2. Else theme from game settings (backend). 3. Else `default`.
    let themeName: string | null = null
    if (!urlTheme) {
      try {
        const gameStore = useGameStore()
        if (gameStore.getBetSettings?.theme) {
          themeName = gameStore.getBetSettings.theme
        } else if (account) {
          try {
            await gameStore.fetchGameSettings(false)
            themeName = gameStore.getBetSettings?.theme || null
          } catch (e) {
            console.warn('Failed to fetch game settings for theme:', e)
          }
        }
      } catch (e) {
        console.warn('Error accessing game store for theme:', e)
      }
    }

    const resolvedThemeName = urlTheme ? resolveThemeName(urlTheme) : resolveThemeName(themeName ?? undefined)

    if (
      !force &&
      resolvedThemeName === currentThemeName.value &&
      lastInitializedAccount === account &&
      isFileBackedTheme(currentTheme.value)
    ) {
      isInitializing = false
      return
    }

    const theme = await loadThemeWithFallback(resolvedThemeName)
    await setTheme(theme.name, true)
    currentTheme.value = theme
    lastInitializedAccount = account
  } catch (error) {
    console.error('Failed to initialize theme:', error)
    const defaultTheme = getTheme('default')
    await setTheme('default', false)
    currentTheme.value = defaultTheme
  } finally {
    isInitializing = false
  }
}

export async function setTheme(themeName: string, loadFromFile = true): Promise<void> {
  try {
    const theme = loadFromFile
      ? await loadThemeWithFallback(themeName)
      : getTheme(themeName) || getTheme('default')
    
    currentThemeName.value = themeName
    currentTheme.value = theme
    applyTheme(theme, getActiveThemeMode())
  } catch (error) {
    console.error(`Failed to set theme ${themeName}:`, error)
    const defaultTheme = getTheme('default')
    currentThemeName.value = 'default'
    currentTheme.value = defaultTheme
    applyTheme(defaultTheme, getActiveThemeMode())
  }
}

export function useTheme() {
  const getAssetPath = (path: keyof Theme['assets'] | string, ...args: unknown[]): string => {
    const assets = currentTheme.value.assets
    const themeName = currentThemeName.value
    
    if (typeof path !== 'string' || !(path in assets)) {
      return ''
    }
    
    const asset = assets[path as keyof Theme['assets']]
    
    if (typeof asset === 'function') {
      return (asset as (...args: unknown[]) => string)(...args)
    }
    
    if (typeof asset === 'string') {
      let resolvedPath = asset
      const locale = args[0] && typeof args[0] === 'string' ? args[0] : getCurrentLocale()
      resolvedPath = resolvedPath.replace('{locale}', locale)
      resolvedPath = resolvedPath.replace('/locale/en/', `/locale/${locale}/`)
      
      if (resolvedPath.includes('/themes/')) {
        // Replace any theme name in the path with the current theme name
        resolvedPath = resolvedPath.replace(/\/themes\/[^/]+/, `/themes/${themeName}`)
        return resolvedPath
      }
      
      if (themeName !== 'default' && resolvedPath.startsWith('/img/')) {
        return resolvedPath.replace('/img/', `/themes/${themeName}/img/`)
      }
      
      return resolvedPath
    }
    
    if (typeof asset === 'object' && asset !== null) {
      const nestedKey = args[0] as string
      const nestedAsset = (asset as Record<string, unknown>)[nestedKey]
      
      if (!nestedAsset) return ''
      
      if (typeof nestedAsset === 'function') {
        const result = (nestedAsset as (...args: unknown[]) => string)(...args.slice(1))
        if (result.includes('/themes/')) {
          // Replace any theme name in the path with the current theme name
          return result.replace(/\/themes\/[^/]+/, `/themes/${themeName}`)
        }
        if (result.startsWith('/img/') && themeName !== 'default') {
          return result.replace('/img/', `/themes/${themeName}/img/`)
        }
        return result
      }
      
      if (typeof nestedAsset === 'string') {
        let resolvedPath = nestedAsset
        const locale = args[1] && typeof args[1] === 'string' ? args[1] : getCurrentLocale()
        resolvedPath = resolvedPath.replace('{locale}', locale)
        resolvedPath = resolvedPath.replace('/locale/en/', `/locale/${locale}/`)
        
        if (resolvedPath.includes('/themes/')) {
          // Replace any theme name in the path with the current theme name
          resolvedPath = resolvedPath.replace(/\/themes\/[^/]+/, `/themes/${themeName}`)
          return resolvedPath
        }
        if (resolvedPath.startsWith('/img/') && themeName !== 'default') {
          return resolvedPath.replace('/img/', `/themes/${themeName}/img/`)
        }
        return resolvedPath
      }
      
      return ''
    }
    
    return ''
  }

  return {
    currentTheme: computed(() => currentTheme.value),
    currentThemeName: computed(() => currentThemeName.value),
    setTheme,
    applyTheme,
    getAssetPath,
  }
}
